import Container from '@mui/material/Container';
import { useDispatch } from 'react-redux';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDailyStateSelectedLocation } from '../../../store/orders/selectors';
import { ordersActions } from '../../../store/orders/slice';
import { getMembers } from '../../../store/team/selectors';
import {
  BestSalesman, DailyTotals, EmployeesRates, SalesByChannel,
} from './components';
import { LocationsSales } from './components/LocationsSales';
import { LocationSalesPercentage } from './components/LocationSalesPercentage';
import { EmployeeSales } from './components/EmployeeSales';
import { MemoizedFilterTabs } from '../../../components/base';
import { getCommissionTeams } from '../../../store/commissions/selectors';
import { useCommon } from '../../../hooks/useCommon';
import { INDEX_TO_COLOR } from '../../../data';

const DailyStats = () => {
  const members = useAppSelector(getMembers);
  const dispatch = useDispatch();
  const dailyStateSelectedLocation = useAppSelector(getDailyStateSelectedLocation);
  const { t } = useTranslation('common');
  const { locationsWithColors, locationsByPermissions } = useCommon();
  const commissionTeams = useAppSelector(getCommissionTeams);

  const membersByLocation = useMemo(() => {
    const teamMemberEmails = new Set(
      commissionTeams.flatMap((team) => team.members.map((member) => member.email)),
    );

    return members.filter((member) => {
      // Skip members in teams
      if (teamMemberEmails.has(member.email)) {
        return false;
      }

      if (dailyStateSelectedLocation === 0) {
        return true;
      }

      const { locations } = member;
      const personnelLocations = locations?.map((location) => location.id);
      return personnelLocations?.includes(dailyStateSelectedLocation);
    });
  }, [dailyStateSelectedLocation, members, commissionTeams]);

  const handleFilterStatus = (newValue: number) => {
    dispatch(ordersActions.setDailyStateSelectedLocation(newValue));
  };

  const getLengthByStatus = (locationId: number) => members.reduce(
    (count, item) => (item.locations.some(({ id }) => id === locationId) ? count + 1 : count),
    0,
  );

  const tabs = useMemo(() => {
    const baseTabs = locationsByPermissions?.map((location, index) => ({
      value: location.id,
      label: location.name,
      color: INDEX_TO_COLOR[index],
      count: getLengthByStatus(location.id),
    }));

    baseTabs.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: members.length,
    });

    return baseTabs;
  }, [locationsByPermissions]);

  const locationName = useMemo(() => locationsByPermissions.find((location) => location.id === dailyStateSelectedLocation)?.name || '', [dailyStateSelectedLocation]);

  return (
    <Container
      sx={{
        px: { xs: 1, lg: 2 },
      }}
      maxWidth={false}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} isDisplayBackground={false} />
      </Stack>
      <Grid container spacing={2}>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <DailyTotals />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <EmployeesRates members={membersByLocation} />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <EmployeeSales members={membersByLocation} locationsWithColors={locationsWithColors} />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <SalesByChannel locationsWithColors={locationsWithColors} />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 6, lg: 6,
          }}
        >
          <BestSalesman />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 6, lg: 6,
          }}
        >
          <LocationSalesPercentage locationName={locationName} locationsWithColors={locationsWithColors} />
        </Grid>
        {dailyStateSelectedLocation === 0 && (
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <LocationsSales locationsWithColors={locationsWithColors} />
        </Grid>
        )}
      </Grid>

    </Container>
  );
};

export default DailyStats;
